// @ts-strict-ignore
import * as React from 'react';
import { useSelector } from 'react-redux';
import Layout, { Content, Footer } from 'antd/lib/layout/layout';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import Skeleton from 'antd/lib/skeleton/Skeleton';
import { EditOutlined } from '@ant-design/icons';
import { RootState } from '../reducers/reducers';
import { ReleaseStatus, ReleaseStepKickoff, ReleaseStepKickoffData, ReleaseTimelinePhase } from '../types';
import { getReleaseStepFromState } from '../helpers';
import {
    selectRelease,
    selectReleasePilotForRelease,
    selectIsWorkflowsSSRStepsEnabled,
    selectStepRun,
} from '../selectors/selectors';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import { FeatureSection } from '@/typescriptHelpers';
import styles from './styles/stepStyles';
import { antGrey, runwayColors } from './styles/globalStyles';
import { displayReleasePilotName } from '@/helpers/displayHelpers';
import PilotMissingPermissionsWarning from '@/components/PilotMissingPermissionsWarning';
import PlanningSummaryModal from './PlanningSummaryModal';
import UserAvatar from './UserAvatar';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { ReleaseEventsTimelineSection } from './releaseOverview/ReleaseTimelineSection';
import { ReleaseTimelineSectionContext } from './timelineEvents/EventsTimeline';
import KickoffInfo from './kickoff/KickoffInfo';
import ReleaseActionsComponent from './sharedComponents/ReleaseActionsComponent';
import { EmptyStateWrapper } from './releaseEmptyState/EmptyStateWrapper';
import KickoffStepFooter from './KickoffStepFooter';
import MarkdownParagraphWithEllipsis from './HOCs/MarkdownParagraphWithEllipsis';

const moduleTitleStyle: React.CSSProperties = {
    fontSize: 13,
};

const StepContentKickoff: React.FC<ReleaseStepOrStepRunComponentProps> = (props) => {
    const { orgId, appId, releaseId, releaseStepType, stepRunComponentProps } = props;
    const stepRunId = stepRunComponentProps?.stepRunId;
    const [versionSummaryModalVisible, setVersionSummaryModalVisible] = React.useState(false);

    const release = useSelector((state: RootState) => selectRelease(state, releaseId));
    const releaseStep = useSelector((state: RootState) =>
        getReleaseStepFromState(releaseStepType, releaseId, state),
    ) as ReleaseStepKickoff;
    const releasePilot = useSelector((state: RootState) => selectReleasePilotForRelease(state, appId, releaseId));
    const isWorkflowsSSRStepsEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsSSRStepsEnabled(state, appId),
    );
    const stepRun = useSelector((state: RootState) => (stepRunId ? selectStepRun(state, stepRunId) : null));

    const stepData: ReleaseStepKickoffData =
        stepRun ? { ...stepRun.data.summaryData, ...stepRun.data.complementaryData } : releaseStep?.data;

    if (!release || !stepData) {
        return (
            <Layout>
                <Content style={{ ...styles.innerContent, paddingTop: 24 }}>
                    <Skeleton title paragraph={{ rows: 8 }} active />
                </Content>
            </Layout>
        );
    }

    const releaseStatus = release.status;
    const releaseType = release.type;
    const rollbackTargetVersionInfo = release.rollbackTargetVersionInfo;

    const editDeleteReleaseDisabled =
        release.status === ReleaseStatus.Skipped || release.status === ReleaseStatus.Completed;
    const completeReleaseDisabled = editDeleteReleaseDisabled || release.timelinePhase !== ReleaseTimelinePhase.Current;

    const canBeSubmitted = !!stepData.canBeSubmitted;
    const workingBranch = stepData.workingBranch ?? '';
    const planningSummary = stepData.releasePlanningSummary ?? undefined;
    const showFooter = !!stepData.workingBranch;

    const cardStyle: React.CSSProperties = {
        backgroundColor: antGrey.grey3,
        borderRadius: 6,
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 24,
        paddingRight: 24,
        flexGrow: 1,
    };

    const cardContainerStyle: React.CSSProperties = {
        flexShrink: 0,
        display: 'flex',
        minWidth: 300,
    };

    const contentStyle: React.CSSProperties = showFooter ? { ...styles.contentWithFooterStyle } : {};

    return (
        <Layout>
            <Content
                style={{
                    ...styles.innerContent,
                    ...contentStyle,
                    display: 'flex',
                    flexDirection: 'column',
                    paddingTop: 0,
                }}
            >
                <Space direction="vertical" size="large">
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Space direction="vertical" size={16} style={{ paddingTop: 28 }}>
                            <Space direction="vertical">
                                <Text strong style={{ ...styles.stepContentStatusText }}>
                                    Version {release.displayableVersion}
                                    {!!release.releaseName && release.releaseName !== '' ?
                                        ' - "' + release.releaseName + '"'
                                    :   ''}
                                </Text>
                            </Space>
                            {!isWorkflowsSSRStepsEnabled && (
                                <ReleaseActionsComponent
                                    orgId={orgId}
                                    appId={appId}
                                    releaseId={releaseId}
                                    version={release.version}
                                    releaseType={releaseType}
                                    editReleaseDisabled={editDeleteReleaseDisabled}
                                    deleteReleaseDisabled={editDeleteReleaseDisabled}
                                    completeReleaseDisabled={completeReleaseDisabled}
                                    rollbackTargetVersionInfo={rollbackTargetVersionInfo}
                                    canBeSubmitted={canBeSubmitted}
                                    canRevertCompletedStatus={release.canRevertCompletedStatus}
                                    releaseStatus={releaseStatus}
                                />
                            )}
                        </Space>
                        <ReleaseEventsTimelineSection
                            orgId={orgId}
                            appId={appId}
                            releaseId={releaseId}
                            stepRunId={stepRunComponentProps?.stepRunId}
                            context={ReleaseTimelineSectionContext.ReleaseStep}
                            releaseStepType={releaseStepType}
                        />
                    </div>
                    <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'start' }}>
                        <KickoffInfo
                            {...stepData}
                            releaseType={releaseType}
                            orgId={orgId}
                            appId={appId}
                            releaseId={releaseId}
                            releaseStepType={releaseStepType}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
                            <div style={{ ...cardContainerStyle, marginBottom: 24 }}>
                                <div style={{ ...cardStyle }}>
                                    <Space direction="vertical" size="large">
                                        <Text style={{ ...moduleTitleStyle }} type="secondary">
                                            Release pilot
                                        </Text>
                                        {(releasePilot || release.releasePilotId) && (
                                            <Space size={16}>
                                                <UserAvatar imageUrl={releasePilot?.commsAvatars?.image48} />
                                                <Space direction="vertical" size={0}>
                                                    <Space size={8}>
                                                        <Text>
                                                            {displayReleasePilotName(
                                                                releasePilot,
                                                                release.releasePilotId,
                                                                true,
                                                            )}
                                                        </Text>
                                                        <PilotMissingPermissionsWarning
                                                            appId={appId}
                                                            pilotId={release.releasePilotId}
                                                        />
                                                    </Space>
                                                </Space>
                                            </Space>
                                        )}
                                        {!release.releasePilotId && (
                                            <Text type="secondary" style={{ fontStyle: 'italic' }}>
                                                No release pilot selected.
                                            </Text>
                                        )}
                                    </Space>
                                </div>
                            </div>
                            <div style={{ ...cardContainerStyle, paddingTop: 0 }}>
                                <div style={{ ...cardStyle }}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                marginBottom: 16,
                                            }}
                                        >
                                            <Text style={{ ...moduleTitleStyle }} type="secondary">
                                                Release description
                                            </Text>
                                            <PermissionedActionButton
                                                actionType={RunwayUserAction.UAEditReleasePlanningSummary}
                                                icon={<EditOutlined style={{ color: runwayColors.info }} />}
                                                onClick={() => setVersionSummaryModalVisible(true)}
                                                releaseId={releaseId}
                                            >
                                                Edit
                                            </PermissionedActionButton>
                                        </div>
                                        <div style={{ flexBasis: 'calc(100% - 2em)' }}>
                                            {planningSummary ?
                                                <MarkdownParagraphWithEllipsis
                                                    content={planningSummary}
                                                    maxHeight="250px"
                                                    backgroundColor={antGrey.grey3}
                                                />
                                            :   <Text style={{ fontStyle: 'italic' }} type="secondary">
                                                    No notes added.
                                                </Text>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Space>
            </Content>
            <EmptyStateWrapper
                releaseStepType={releaseStepType}
                appId={appId}
                featureSections={[FeatureSection.KickoffCTAs]}
                releaseId={releaseId}
                shouldShowAsTooltip
                customWrapperStyle={{ position: 'absolute' }}
            >
                {(isInEmptyState) =>
                    (isInEmptyState || showFooter) && (
                        <Footer style={styles.footerStyle}>
                            <KickoffStepFooter
                                orgId={orgId}
                                appId={appId}
                                releaseId={releaseId}
                                releaseStepType={releaseStepType}
                                stepRunComponentProps={stepRunComponentProps}
                                workingBranch={workingBranch}
                                shouldShowEmptyState={isInEmptyState}
                            />
                        </Footer>
                    )
                }
            </EmptyStateWrapper>
            <PlanningSummaryModal
                orgId={orgId}
                appId={appId}
                releaseId={releaseId}
                open={versionSummaryModalVisible}
                versionSummary={planningSummary}
                displayableVersion={release.displayableVersion}
                onCancel={() => setVersionSummaryModalVisible(false)}
                onDidUpdate={() => setVersionSummaryModalVisible(false)}
            />
        </Layout>
    );
};

export default StepContentKickoff;
